import { keepPreviousData } from "@tanstack/react-query";
import { motion } from "motion/react";
import { toast } from "sonner";
import { ArrowRight, Calendar, CalendarPlus, CalendarX, ChevronLeftIcon, ChevronRightIcon, Clock, CloudIcon, ExternalLink, Eye, Loader2, Mail, MapPin, MoonIcon, Phone, SunIcon, Tag, User } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, addMonths, subMonths, addDays, startOfDay } from "date-fns";
import { trpc } from "~/api/trpc";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import { formatPractitionerName, type PastPractitionerOption, PastPractitionerPickerRow } from "~/components/new/PastPractitionerPicker";
import TopProgressBar from "~/components/new/TopProgressBar";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "~/components/ui/alert-dialog";
import { Separator } from "~/components/ui/separator";
import LocalDateTime from "~/lib/localdatetime";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import { AppointmentStatus } from "~/types/server-types/AppointmentStatus";

type Appointment = {
    id: string;
    title: string;
    bookedPrice: number;
    status: AppointmentStatus;
    messageToCustomer: string | null;
    messageFromCustomer: string | null;
    couponCodes: string | null;
    startTimeLocalSerialized: string;
    endTimeLocalSerialized: string;
    isDoctorAppointment: boolean;
    customer: {
        phone: string | null;
        email: string | null;
        firstName: string;
        lastName: string;
    };
    variants: {
        id: string;
        name: string;
    }[];
    store: {
        id: number | 'digital';
        name: string;
        fullAddress: string;
        reviewsCount: number;
        reviewsStarsOutOfFive: number;
        images: string[];
        clinicGoogleMapsImgSrc: string;
        googleMapsUrl: string;
    };
};

interface Timeslot {
    startTime: LocalDateTime;
    startTimeStr: string;
}

type TimeOfDay = 'morning' | 'afternoon' | 'evening';

type ChangeAppointmentWidgetProps = {
    appointmentId: string;
    storeId: number | 'digital';
    onSelectDate: (date: LocalDateTime | undefined) => void;
    variantIds: string[];
    practitionerId: number | null;
}

const ChangeAppointmentWidget = ({
    appointmentId,
    storeId,
    onSelectDate,
    variantIds,
    practitionerId,
}: ChangeAppointmentWidgetProps) => {
    const { language } = useLanguageStore();

    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<LocalDateTime | null>(null);
    const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<TimeOfDay>('morning');
    const [showMoreTimeslots, setShowMoreTimeslots] = useState(false);
    const [timeslotViewMode, setTimeslotViewMode] = useState<"day-view" | "month-view">('day-view');
    const [selectedTimeslot, setSelectedTimeslot] = useState<LocalDateTime | undefined>(undefined);

    const dateScrollContainerRef = useRef<HTMLDivElement>(null);

    const queryParams = useMemo(() => ({
        storeId: storeId,
        from: startOfMonth(currentMonth).toISOString(),
        to: (() => {
            const fromDate = new Date();
            const endOfMonthDate = endOfMonth(currentMonth);
            const minEndDate = addDays(fromDate, 14);
            return (isBefore(endOfMonthDate, minEndDate) ? minEndDate : endOfMonthDate).toISOString();
        })(),
        variantIds: variantIds,
        existingAppointmentId: appointmentId,
        ...(practitionerId ? { practitionerId } : {}),
    }), [storeId, variantIds, currentMonth, appointmentId, practitionerId]);

    const availabilitiesQuery = trpc.publicAppointments.getAvailabilities.useQuery(queryParams, {
        placeholderData: keepPreviousData,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        staleTime: 1 * 60 * 1000,
        refetchInterval: 1 * 60 * 1000,
    });

    const timeslotsByDate: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = useMemo(() => {
        if (!availabilitiesQuery.data) {
            return {};
        }

        const rawAvailabilitiesData: { start: LocalDateTime, priorityCategory: number }[] = availabilitiesQuery.data.map((timeslot) => ({
            start: LocalDateTime.deserialize(timeslot?.startTimeSerialized),
            priorityCategory: timeslot.priorityCategory,
        }));
        const dateMap: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = {};

        const currentMonthStr = format(currentMonth, "yyyy-MM");

        rawAvailabilitiesData.filter((val) => {
            if (!val) { return false; }
            const dateKey = val.start.getDisplayDate();
            const monthStr = format(new Date(dateKey), "yyyy-MM");
            if (monthStr < currentMonthStr) { return false; }
            return true;
        }).forEach(timeslotData => {
            const dateKey = timeslotData.start.getDisplayDate();

            if (!(dateKey in dateMap)) {
                dateMap[dateKey] = [];
            }

            const timeslot = {
                startTime: timeslotData.start,
                startTimeStr: timeslotData.start.format("HH:mm")
            };

            dateMap[dateKey].push({
                timeslot: timeslot,
                priorityCategory: timeslotData.priorityCategory
            });
        });

        Object.keys(dateMap).forEach(dateKey => {
            dateMap[dateKey].sort((a, b) => {
                const aTime = a.timeslot.startTime.toUTC().getTime();
                const bTime = b.timeslot.startTime.toUTC().getTime();
                return aTime - bTime;
            });
        });

        return dateMap;
    }, [availabilitiesQuery.data, currentMonth]);

    const scrollToSelectedDate = (date: LocalDateTime) => {
        if (!dateScrollContainerRef.current) return;
        const container = dateScrollContainerRef.current;
        const dateKey = date.format('yyyy-MM-dd');
        const dateButton = container.querySelector(`[data-date="${dateKey}"]`) as HTMLElement;
        if (dateButton) {
            const containerRect = container.getBoundingClientRect();
            const buttonRect = dateButton.getBoundingClientRect();
            const containerCenter = containerRect.width / 2;
            const buttonCenter = buttonRect.left - containerRect.left + buttonRect.width / 2;
            const scrollOffset = buttonCenter - containerCenter;
            container.scrollTo({
                left: container.scrollLeft + scrollOffset,
                behavior: 'smooth'
            });
        }
    };

    const [hasTriedToSelectNextMonth, setHasTriedToSelectNextMonth] = useState<boolean>(false);
    useEffect(() => {
        if (!availabilitiesQuery?.data || hasTriedToSelectNextMonth) { return; }
        if (availabilitiesQuery.data.length === 0) {
            setCurrentMonth(addMonths(currentMonth, 1));
            setHasTriedToSelectNextMonth(true);
        }
    }, [availabilitiesQuery?.data]);

    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    useEffect(() => {
        if (hasInitialized || !timeslotsByDate || Object.keys(timeslotsByDate).length === 0) { return; }
        const sortedKeys = Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b));
        if (sortedKeys.length > 0 && timeslotsByDate[sortedKeys[0]].length > 0) {
            const firstAvailableTimeslot = timeslotsByDate[sortedKeys[0]][0];
            const firstAvailableDate = firstAvailableTimeslot.timeslot.startTime;
            setSelectedDate(firstAvailableDate);

            const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
            if (hour < 12) { setSelectedTimeOfDay('morning'); }
            else if (hour >= 12 && hour < 16) { setSelectedTimeOfDay('afternoon'); }
            else { setSelectedTimeOfDay('evening'); }

            setHasInitialized(true);
            setTimeout(() => {
                const curMonthStr = format(currentMonth, "yyyy-MM");
                const firstTimeslotDate = new Date(firstAvailableTimeslot.timeslot.startTime.getDisplayDate());
                const firstTimeslotMonthStr = format(firstTimeslotDate, "yyyy-MM");
                if (curMonthStr !== firstTimeslotMonthStr) {
                    setCurrentMonth(firstTimeslotDate);
                }
                scrollToSelectedDate(firstAvailableDate);
            }, 100);
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        if (!selectedDate || Object.keys(timeslotsByDate).length === 0) { return; }
        const timeslotDateKey = selectedDate.format('yyyy-MM-dd');
        const timeslotsOnDate = timeslotsByDate[timeslotDateKey];
        if (!timeslotsOnDate || timeslotsOnDate.length === 0) { return; }

        const firstAvailableTimeslot = timeslotsOnDate[0];
        const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
        if (hour < 12) { setSelectedTimeOfDay('morning'); }
        else if (hour >= 12 && hour < 16) { setSelectedTimeOfDay('afternoon'); }
        else { setSelectedTimeOfDay('evening'); }
    }, [selectedDate]);

    useEffect(() => {
        if (
            timeslotViewMode === 'day-view' &&
            !availabilitiesQuery.isFetching &&
            selectedDate &&
            dateScrollContainerRef.current
        ) {
            const timeoutId = setTimeout(() => {
                scrollToSelectedDate(selectedDate);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [timeslotViewMode, availabilitiesQuery.isFetching, selectedDate]);

    useEffect(() => {
        setSelectedTimeOfDay("morning");
        setShowMoreTimeslots(false);
        setSelectedDate(null);
    }, [currentMonth]);

    useEffect(() => {
        if (Object.keys(timeslotsByDate).length > 0) {
            const sortedKeys = Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b));
            if (timeslotsByDate[sortedKeys[0]].length > 0) {
                const firstAvailableTimeslot = timeslotsByDate[sortedKeys[0]][0];
                const firstAvailableDate = firstAvailableTimeslot.timeslot.startTime;
                setSelectedDate(firstAvailableDate);
            }
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        setShowMoreTimeslots(false);
        setSelectedTimeslot(undefined);
        onSelectDate(undefined);
    }, [selectedDate, selectedTimeOfDay]);

    const monthDays = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const today = new Date();
        return eachDayOfInterval({ start, end })
            .filter(day => !isBefore(day, today) || isSameDay(day, today))
            .map(day => format(day, 'yyyy-MM-dd'));
    }, [currentMonth]);

    const getAppointmentsCount = (date: LocalDateTime, timeOfDay?: TimeOfDay) => {
        const dateKey = date.getDisplayDate();
        const timeslots = timeslotsByDate[dateKey] || [];
        if (timeOfDay) {
            return timeslots.filter(slot => {
                const hour = slot.timeslot.startTime.getHours();
                switch (timeOfDay) {
                    case 'morning': return hour < 12;
                    case 'afternoon': return hour >= 12 && hour < 16;
                    case 'evening': return hour >= 16;
                }
            }).length;
        }
        return timeslots.length;
    };

    const getTimeslotScore = (date: LocalDateTime) => {
        const minutes = date.getMinutes();
        let score = 0;
        if (minutes % 5 !== 0) return -1;
        if (minutes === 0) score += 100;
        else if (minutes % 30 === 0) score += 80;
        else if (minutes % 15 === 0) score += 60;
        else if (minutes % 10 === 0) score += 40;
        else if (minutes % 5 === 0) score += 20;
        return score;
    };

    const selectBestTimeslots = (timeslots: { timeslot: Timeslot; priorityCategory: number; }[], maxCount: number) => {
        if (timeslots.length <= maxCount) return timeslots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());

        const byCategory = new Map<number, typeof timeslots>();
        for (const slot of timeslots) {
            if (!byCategory.has(slot.priorityCategory)) {
                byCategory.set(slot.priorityCategory, []);
            }
            byCategory.get(slot.priorityCategory)!.push(slot);
        }

        for (const slots of byCategory.values()) {
            slots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
        }

        const selected: typeof timeslots = [];
        let remaining = maxCount;

        const sortedCategories = [...byCategory.keys()].sort((a, b) => a - b);
        for (const category of sortedCategories) {
            if (remaining <= 0) break;
            const categorySlots = byCategory.get(category)!;

            if (categorySlots.length <= remaining) {
                selected.push(...categorySlots);
                remaining -= categorySlots.length;
            } else {
                const candidates = [...categorySlots];
                for (let i = 0; i < remaining; i++) {
                    if (selected.length === 0) {
                        const idx = Math.floor(candidates.length / 2);
                        selected.push(candidates.splice(idx, 1)[0]);
                    } else {
                        let bestIdx = 0;
                        let bestMinDist = -1;
                        let bestGapSize = -1;
                        const selectedTimes = selected.map(s => s.timeslot.startTime.toUTC().getTime()).sort((a, b) => a - b);
                        for (let c = 0; c < candidates.length; c++) {
                            const candidateTime = candidates[c].timeslot.startTime.toUTC().getTime();
                            let minDist = Infinity;
                            for (const t of selectedTimes) {
                                const dist = Math.abs(candidateTime - t);
                                if (dist < minDist) minDist = dist;
                            }
                            let gapSize = 0;
                            if (candidateTime <= selectedTimes[0]) {
                                gapSize = selectedTimes[0] - candidateTime;
                            } else if (candidateTime >= selectedTimes[selectedTimes.length - 1]) {
                                gapSize = candidateTime - selectedTimes[selectedTimes.length - 1];
                            } else {
                                for (let j = 0; j < selectedTimes.length - 1; j++) {
                                    if (candidateTime >= selectedTimes[j] && candidateTime <= selectedTimes[j + 1]) {
                                        gapSize = selectedTimes[j + 1] - selectedTimes[j];
                                        break;
                                    }
                                }
                            }
                            if (minDist > bestMinDist || (minDist === bestMinDist && gapSize > bestGapSize)) {
                                bestMinDist = minDist;
                                bestGapSize = gapSize;
                                bestIdx = c;
                            }
                        }
                        selected.push(candidates.splice(bestIdx, 1)[0]);
                    }
                }
                remaining = 0;
            }
        }

        return selected.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    const getTimeslotsForSelectedDateTime = () => {
        if (!selectedDate) return [];
        const dateKey = selectedDate.format('yyyy-MM-dd');
        const dayTimeslots = timeslotsByDate[dateKey] || [];
        const filteredByTime = dayTimeslots.filter(slot => {
            const hour = slot.timeslot.startTime.getHours();
            switch (selectedTimeOfDay) {
                case 'morning': return hour < 12;
                case 'afternoon': return hour >= 12 && hour < 16;
                case 'evening': return hour >= 16;
                default: return false;
            }
        });
        return filteredByTime.filter(slot => getTimeslotScore(slot.timeslot.startTime) >= 0).sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    const filteredTimeslots = getTimeslotsForSelectedDateTime();
    const displayedTimeslots = showMoreTimeslots ? filteredTimeslots : selectBestTimeslots(filteredTimeslots, 4);
    const hasMoreTimeslots = filteredTimeslots.length > 4;

    const canGoPrevious = !isBefore(subMonths(currentMonth, 1), startOfMonth(new Date()));

    const timeOfDayOptions = [
        { id: 'morning' as TimeOfDay, label: language === Language.GERMAN ? 'Vormittag' : 'Morning', icon: SunIcon, range: '8-12' },
        { id: 'afternoon' as TimeOfDay, label: language === Language.GERMAN ? 'Nachmittag' : 'Afternoon', icon: CloudIcon, range: '12-16' },
        { id: 'evening' as TimeOfDay, label: language === Language.GERMAN ? 'Abend' : 'Evening', icon: MoonIcon, range: '16-20' },
    ];

    const handleSelectTimeslot = (timeslot: LocalDateTime) => {
        setSelectedTimeslot(timeslot);
        onSelectDate(timeslot);
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="relative w-full bg-white p-4 rounded-[5px] flex flex-col gap-1 drop-shadow-md">
                <div className='w-full flex flex-row items-center justify-between'>
                    <span className='text-lg font-light text-burgundy'>
                        {language === Language.GERMAN ? 'Wähle deinen Termin' : 'Select your appointment'}
                    </span>
                    <div className='flex flex-row items-center bg-burgundy/10 rounded-[5px] p-1'>
                        <button
                            className={`p-2 rounded-[5px] ${timeslotViewMode === 'day-view' ? 'bg-white' : ''}`}
                            onClick={() => setTimeslotViewMode('day-view')}
                        >
                            <Eye className="w-4 h-4 text-burgundy" />
                        </button>
                        <button
                            className={`p-2 rounded-[5px] ${timeslotViewMode === 'month-view' ? 'bg-white' : ''}`}
                            onClick={() => setTimeslotViewMode('month-view')}
                        >
                            <Calendar className="w-4 h-4 text-burgundy" />
                        </button>
                    </div>
                </div>

                <Separator className='my-2' />

                <div className='relative overflow-x-auto' ref={dateScrollContainerRef}>
                    {availabilitiesQuery.isFetching ? (
                        timeslotViewMode === 'day-view' ? (
                            <div className='flex gap-3 pb-4' style={{ minWidth: 'max-content' }}>
                                {[...Array(7)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-col items-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 animate-pulse"
                                    >
                                        <div className="w-8 h-5 bg-burgundy/20 rounded" />
                                        <div className="w-10 h-3 bg-burgundy/20 rounded mt-1" />
                                        <div className="w-2 h-2 rounded-full bg-burgundy/20 mt-1" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='flex flex-col gap-4 pb-4 overflow-x-scroll'>
                                <div className='flex flex-row items-center justify-between'>
                                    <div className="w-32 h-5 bg-burgundy/20 rounded animate-pulse" />
                                    <div className='flex flex-row items-center gap-2'>
                                        <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                        <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                    </div>
                                </div>
                                <div className='grid grid-cols-7 gap-0.5'>
                                    {(language === Language.GERMAN
                                        ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                        : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                    ).map((day) => (
                                        <div key={day} className='flex items-center justify-center py-2'>
                                            <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className='grid grid-cols-7 gap-0.5'>
                                    {[...Array(35)].map((_, i) => (
                                        <div key={i} className='flex flex-col items-center justify-center py-2 animate-pulse'>
                                            <div className="w-6 h-4 bg-burgundy/20 rounded" />
                                            <div className="w-1.5 h-1.5 rounded-full bg-burgundy/20 mt-1" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ) : timeslotViewMode === 'day-view' ? (
                        <div className='flex gap-3 pb-4 w-max'>
                            {isAfter(startOfMonth(currentMonth), startOfMonth(new Date())) && (
                                <button
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                    className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 text-burgundy" />
                                </button>
                            )}
                            {monthDays.map((dayStr, index) => {
                                const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin');
                                const appointmentsCount = getAppointmentsCount(dayLocal);
                                const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());
                                const isSunday = dayLocal.toUTC().getDay() === 0;
                                const isLastDayOfMonth = index === monthDays.length - 1;
                                const showWeekDivider = isSunday && !isLastDayOfMonth;

                                return (
                                    <div key={dayStr} className="flex items-center gap-3">
                                        <button
                                            data-date={dayStr}
                                            onClick={() => setSelectedDate(dayLocal)}
                                            className={`flex flex-col items-center gap-1 p-2 rounded-[5px] min-w-[40px] relative ${isSelected
                                                ? 'border-2 border-burgundy text-black font-medium pb-4'
                                                : 'border border-burgundy/30 hover:border-burgundy/50'
                                                }`}
                                        >
                                            <span className={`text-xs text-burgundy ${isSelected ? 'font-medium' : 'font-light'}`}>
                                                {dayLocal.format("EEEEEE", { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            <span className={`text-[13px] text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                {dayLocal.format("d MMM", { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            {appointmentsCount > 0 ? (
                                                <div className='w-2 h-2 rounded-full bg-[#20A26C] mt-1' />
                                            ) : (
                                                <div className='w-2 h-2 rounded-full bg-light-gray mt-1' />
                                            )}
                                        </button>
                                        {showWeekDivider && (
                                            <div className='h-8 w-px bg-burgundy/20' />
                                        )}
                                    </div>
                                );
                            })}
                            <button
                                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                            >
                                <ChevronRightIcon className="h-5 w-5 text-burgundy" />
                            </button>
                        </div>
                    ) : (
                        <div className='flex flex-col gap-2'>
                            <div className='flex flex-row items-center justify-between'>
                                <span className='text-base font-medium text-burgundy'>
                                    {format(currentMonth, "MMMM yyyy", { locale: getDateLocaleByLocale(language) })}
                                </span>
                                <div className='flex flex-row items-center gap-2'>
                                    <button
                                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                        disabled={!canGoPrevious}
                                        className={`w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center ${!canGoPrevious ? 'opacity-40 cursor-not-allowed' : 'hover:border-burgundy/50'}`}
                                    >
                                        <ChevronLeftIcon className="h-4 w-4 text-burgundy" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                        className="w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center hover:border-burgundy/50"
                                    >
                                        <ChevronRightIcon className="h-4 w-4 text-burgundy" />
                                    </button>
                                </div>
                            </div>

                            <div className='grid grid-cols-7 gap-0.5'>
                                {(language === Language.GERMAN
                                    ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                    : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                ).map((day) => (
                                    <div key={day} className='flex items-center justify-center py-1'>
                                        <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                    </div>
                                ))}
                            </div>

                            <div className='grid grid-cols-7 justify-items-center'>
                                {(() => {
                                    const monthStart = startOfMonth(currentMonth);
                                    const monthEnd = endOfMonth(currentMonth);
                                    const today = new Date();

                                    let startDayOfWeek = monthStart.getDay() - 1;
                                    if (startDayOfWeek < 0) startDayOfWeek = 6;

                                    const daysInMonth = monthEnd.getDate();
                                    const cells = [];

                                    for (let i = 0; i < startDayOfWeek; i++) {
                                        cells.push(
                                            <div key={`empty-${i}`} className='flex items-center justify-center py-2' />
                                        );
                                    }

                                    for (let day = 1; day <= daysInMonth; day++) {
                                        const dayStr = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day), 'yyyy-MM-dd');
                                        const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin');
                                        const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

                                        const isPast = isBefore(dayDate, startOfDay(today));
                                        const appointmentsCount = getAppointmentsCount(dayLocal);
                                        const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());

                                        cells.push(
                                            <button
                                                key={dayStr}
                                                data-date={dayStr}
                                                onClick={() => !isPast && setSelectedDate(dayLocal)}
                                                disabled={isPast}
                                                className={`flex flex-col items-center justify-center px-3 py-2 rounded-[6px] border ${isPast ? 'opacity-60 cursor-not-allowed' : ''} ${isSelected ? 'border-burgundy' : 'border-transparent'}`}
                                            >
                                                <span className={`relative text-sm text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                    {day}
                                                    {appointmentsCount === 0 && !isPast && (
                                                        <span className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-burgundy" />
                                                    )}
                                                </span>
                                                {!isPast && (
                                                    appointmentsCount > 0 ? (
                                                        <div className='w-1.5 h-1.5 rounded-full bg-[#20A26C] mt-0.5' />
                                                    ) : (
                                                        <div className='w-1.5 h-1.5 rounded-full bg-light-gray mt-0.5' />
                                                    )
                                                )}
                                            </button>
                                        );
                                    }

                                    return cells;
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedDate && (
                <div className='flex flex-col gap-4 bg-white mt-2 p-4 drop-shadow-md rounded-[5px]'>
                    <span className='text-base font-light text-burgundy'>
                        {language === Language.GERMAN ? 'Verfügbare Termine' : 'Available appointments'}
                    </span>

                    <div className='grid grid-cols-3 gap-3'>
                        {timeOfDayOptions.map((option) => {
                            const Icon = option.icon;
                            const isSelected = selectedTimeOfDay === option.id;
                            const hasTimeslots = timeslotsByDate[selectedDate.getDisplayDate()]?.some(slot => {
                                const hour = slot.timeslot.startTime.getHours();
                                switch (option.id) {
                                    case 'morning': return hour < 12;
                                    case 'afternoon': return hour >= 12 && hour < 16;
                                    case 'evening': return hour >= 16;
                                    default: return false;
                                }
                            }) || false;

                            return (
                                <button
                                    key={option.id}
                                    onClick={() => setSelectedTimeOfDay(option.id)}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-[5px] text-burgundy ${isSelected
                                        ? 'border-2 border-burgundy font-medium scale-110 md:scale-100'
                                        : 'border border-burgundy/50'
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className='text-sm'>{option.range} Uhr</span>
                                    {hasTimeslots ? (
                                        <div className='w-2 h-2 rounded-full bg-[#20A26C]' />
                                    ) : (
                                        <span className='text-xs text-burgundy/60'>
                                            {language === Language.GERMAN ? 'Keine Termine' : 'No slots'}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <Separator />

                    {getAppointmentsCount(selectedDate, selectedTimeOfDay) > 0 ? (
                        <>
                            {filteredTimeslots.length > 0 && (
                                <div className='grid grid-cols-3 min-[350px]:grid-cols-4 gap-4'>
                                    {displayedTimeslots.map((timeslot, index) => {
                                        const isSelected = !selectedTimeslot ? false : selectedTimeslot.toUTC().getTime() === timeslot.timeslot.startTime.toUTC().getTime();

                                        return (
                                            <button
                                                key={`${timeslot.timeslot.startTime.getDisplayDateTime()}-${index}`}
                                                onClick={() => handleSelectTimeslot(timeslot.timeslot.startTime)}
                                                className={`w-full flex justify-center items-center px-2 py-2 rounded-[5px] bg-white ${isSelected
                                                    ? 'border-2 border-burgundy text-burgundy font-medium'
                                                    : 'border border-burgundy/50 text-burgundy/70 font-normal'
                                                    }`}
                                            >
                                                <span className='text-sm font-medium'>
                                                    {timeslot.timeslot.startTimeStr}
                                                </span>
                                            </button>
                                        );
                                    })}

                                    {hasMoreTimeslots && !showMoreTimeslots && (
                                        <button
                                            onClick={() => setShowMoreTimeslots(true)}
                                            className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                        >
                                            {language === Language.GERMAN ? 'Mehr anzeigen' : 'Show more'}
                                        </button>
                                    )}

                                    {showMoreTimeslots && hasMoreTimeslots && (
                                        <button
                                            onClick={() => setShowMoreTimeslots(false)}
                                            className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                        >
                                            {language === Language.GERMAN ? 'Weniger anzeigen' : 'Show less'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='px-2'>
                            <span className='text-burgundy font-light leading-normal text-sm'>
                                {language === Language.GERMAN
                                    ? 'Keine Termine zu dieser Tageszeit verfügbar. Bitte wähle eine andere Tageszeit oder einen anderen Tag.'
                                    : 'No appointments available at this time of day. Please select a different time or day.'}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {availabilitiesQuery.isError && (
                <div className="text-red-600 text-sm mt-2 font-light">
                    Fehler beim Laden der verfügbaren Termine. Bitte passen Sie den Zeitraum an.
                </div>
            )}
        </div>
    );
};

const FixedBottomBar = ({
    viewMode,
    onChangeAppointment,
    onCancelAppointment,
    cancelFormOpen,
    onCancelFormOpenChange,
    onConfirmChange,
    onBackToDetails,
    canBeModified,
    hasSelectedNewTime,
    isLoading
}: {
    viewMode: 'details' | 'changing';
    onChangeAppointment: () => void;
    onCancelAppointment: (reason: string, notes: string) => void;
    cancelFormOpen: boolean;
    onCancelFormOpenChange: (open: boolean) => void;
    onConfirmChange: (reason: string, notes: string) => void;
    onBackToDetails: () => void;
    canBeModified: boolean;
    hasSelectedNewTime: boolean;
    isLoading: boolean;
}) => {

    const modificationReasonOptions: [
        "Terminkonflikt",
        "Krankheit",
        "Sonstiges",
    ] = [
            "Terminkonflikt",
            "Krankheit",
            "Sonstiges",
        ];

    const cancellationReasonOptions: [
        "Terminkonflikt",
        "Krankheit",
        "Andere Klinik gewählt",
        "Kein Interesse mehr an der Leistung",
        "Sonstiges",
    ] = [
            "Terminkonflikt",
            "Krankheit",
            "Andere Klinik gewählt",
            "Kein Interesse mehr an der Leistung",
            "Sonstiges",
        ];

    const changeAlertDialogRef = useRef<HTMLButtonElement>(null);

    const [reason, setReason] = useState<string | undefined>();
    const [note, setNote] = useState<string>("");

    if (viewMode === 'details') {
        if (!canBeModified) { return null; }

        return (
            <div className='w-full flex flex-col gap-3'>
                <PrimaryActionButton
                    onClick={onChangeAppointment}
                    label='TERMIN ÄNDERN'
                    rightIcon={<ArrowRight />}
                />

                <AlertDialog open={cancelFormOpen} onOpenChange={onCancelFormOpenChange}>
                    <AlertDialogTrigger disabled={isLoading}>
                        <button
                            onClick={() => { }}
                            className="flex flex-row items-center justify-center gap-2 w-full px-5 py-3 text-center text-red-600 font-medium border border-red-600 rounded-full hover:bg-red-50 transition-colors"
                        >
                            <CalendarX className='w-4 h-4' />
                            Termin absagen
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-burgundy">Bist du dir sicher?</AlertDialogTitle>
                            <AlertDialogDescription className="text-burgundy/70">
                                Bist du dir sicher, dass du den Termin absagen möchtest?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-normal text-burgundy">Grund für die Änderung*</label>
                                <select
                                    value={reason || ""}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-burgundy/20 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-burgundy/50 text-burgundy"
                                >
                                    <option value="">Grund auswählen...</option>
                                    {cancellationReasonOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {reason === "Sonstiges" && (
                                <div className="space-y-2">
                                    <label className="text-sm font-normal text-burgundy">Bitte Grund angeben*</label>
                                    <textarea
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Grund für die Änderung eingeben..."
                                        rows={3}
                                        className="w-full px-3 py-2 border border-burgundy/20 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-burgundy/50 resize-none text-burgundy placeholder:text-burgundy/50"
                                    />
                                    {note.length < 10 && note.length > 0 && (
                                        <p className="text-sm text-red-600">Mindestens 10 Zeichen erforderlich</p>
                                    )}
                                </div>
                            )}
                        </div>
                        <AlertDialogFooter className='w-full flex justify-center'>
                            <div className='w-full flex flex-col gap-8 justify-center'>
                                <div className='flex flex-row gap-2 items-center'>
                                    <AlertDialogCancel className='w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-base text-burgundy/70 font-medium border border-burgundy/20 rounded-full hover:bg-burgundy/5 transition-colors'>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction
                                        asChild
                                        disabled={!reason || (reason === "Sonstiges" && note.length < 10)}
                                    >
                                        <button
                                            onClick={() => onChangeAppointment()}
                                            disabled={!reason || (reason === "Sonstiges" && note.length < 10)}
                                            className="flex flex-row items-center justify-center gap-1 w-full px-5 py-3 text-center bg-white text-burgundy! font-medium border border-burgundy rounded-full hover:bg-burgundy/5! transition-colors"
                                        >
                                            <CalendarPlus className="w-4 h-4" />
                                            Neue Zeit suchen
                                        </button>
                                    </AlertDialogAction>
                                </div>
                                <div className='w-full flex flex-row justify-center'>
                                    <button
                                        onClick={() => onCancelAppointment(reason || "", note)}
                                        disabled={!reason || (reason === "Sonstiges" && note.length < 10)}
                                        className="w-full text-center text-xs underline text-red-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Termin absagen
                                    </button>
                                </div>
                            </div>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        );
    }

    // Changing mode
    return (
        <div className='fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-4 p-6 pb-8 bg-light-burgundy'>
            <AlertDialog>
                <AlertDialogTrigger ref={changeAlertDialogRef} disabled={!hasSelectedNewTime || isLoading}>
                    <PrimaryActionButton
                        onClick={() => { }}
                        label='NEUE ZEIT BESTÄTIGEN'
                        disabled={!hasSelectedNewTime}
                        loading={isLoading}
                        rightIcon={<ArrowRight />}
                    />
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-burgundy">Bist du dir sicher?</AlertDialogTitle>
                        <AlertDialogDescription className="text-burgundy/70">
                            Bist du dir sicher, dass du den Termin ändern möchtest?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-normal text-burgundy">Grund für die Änderung*</label>
                            <select
                                value={reason || ""}
                                onChange={(e) => setReason(e.target.value)}
                                className="w-full px-3 py-2 border border-burgundy/20 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-burgundy/50 text-burgundy"
                            >
                                <option value="">Grund auswählen...</option>
                                {modificationReasonOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {reason === "Sonstiges" && (
                            <div className="space-y-2">
                                <label className="text-sm font-normal text-burgundy">Bitte Grund angeben*</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Grund für die Änderung eingeben..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-burgundy/20 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-burgundy/50 resize-none text-burgundy placeholder:text-burgundy/50"
                                />
                                {note.length < 10 && note.length > 0 && (
                                    <p className="text-sm text-red-600">Mindestens 10 Zeichen erforderlich</p>
                                )}
                            </div>
                        )}
                    </div>
                    <AlertDialogFooter className='flex items-center'>
                        <AlertDialogCancel className='w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-base text-burgundy/70 font-medium border border-burgundy/20 rounded-full hover:bg-burgundy/5 transition-colors'>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            asChild
                            disabled={!hasSelectedNewTime || !reason || (reason === "Sonstiges" && note.length < 10)}
                        >
                            <PrimaryActionButton
                                onClick={() => onConfirmChange(reason || "", note)}
                                label='Neue Zeit bestätigen'
                                disabled={!hasSelectedNewTime || !reason || (reason === "Sonstiges" && note.length < 10)}
                                loading={isLoading}
                            />
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <button
                onClick={onBackToDetails}
                className="w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-burgundy/70 font-medium border border-burgundy/20 rounded-full hover:bg-burgundy/5 transition-colors"
            >
                Zurück zu Details
            </button>
        </div>
    );
};

const AppointmentDetails = ({ appointment }: { appointment: Appointment }) => {
    const { language } = useLanguageStore();

    const translateStatus = (status?: string | null): string => {
        if (!status) { return 'Unbekannt'; }
        switch (status) {
            case 'SCHEDULED': return 'Geplant';
            case 'REQUESTED': return 'Angefragt';
            case 'CANCELLED': return 'Storniert';
            case 'COMPLETED': return 'Abgeschlossen';
            default: return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SCHEDULED': return 'bg-green-100 text-green-800';
            case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
            case 'CANCELLED': return 'bg-red-100 text-red-800';
            case 'COMPLETED': return 'bg-blue-100 text-blue-800';
            default: return 'bg-burgundy/10 text-burgundy';
        }
    };

    const startTimeLocal = LocalDateTime.deserialize(appointment.startTimeLocalSerialized);
    const endTimeLocal = LocalDateTime.deserialize(appointment.endTimeLocalSerialized);

    return (
        <div className='flex flex-col gap-8'>
            {/* Status */}
            <motion.div
                className='flex flex-col gap-3'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
            >
                <span className='text-lg font-normal text-burgundy'>Status</span>
                <div className='flex items-center gap-2'>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
                        {translateStatus(appointment.status)}
                    </span>
                </div>
            </motion.div>

            {/* Store Details */}
            <motion.div
                className='flex flex-col'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15, ease: "easeOut" }}
            >
                <span className='text-lg font-normal text-burgundy mb-3'>Location</span>
                <div className='rounded-[5px] bg-white border-2 border-burgundy/20 drop-shadow-md overflow-hidden'>
                    {(appointment.store?.images?.length ?? 0) > 0 && (
                        <div className='w-full h-full max-h-40'>
                            <img className='w-full h-full max-h-40 object-cover' src={appointment.store!.images[0]} />
                        </div>
                    )}
                    <div className='p-4'>
                        <span className='text-base text-burgundy font-normal'>{appointment.store?.name || 'Unbekannte Praxis'}</span>
                    </div>
                    <div className='h-px bg-burgundy/50 mx-4' />
                    {!!appointment.store?.fullAddress && appointment.store?.fullAddress.length > 0 && (
                        <>
                            <div
                                className='flex flex-row items-center gap-3 p-4 cursor-pointer hover:bg-burgundy/5 transition-colors'
                                onClick={() => {
                                    if (appointment.store?.googleMapsUrl) {
                                        window.open(appointment.store?.googleMapsUrl, '_blank');
                                    }
                                }}
                            >
                                <MapPin className='shrink-0 w-4 h-4 text-burgundy' />
                                <span className='text-sm text-burgundy font-light flex flex-row items-center gap-1'>
                                    {appointment.store?.fullAddress}
                                    {appointment.store?.googleMapsUrl && <ExternalLink className="w-3 h-3" />}
                                </span>
                            </div>
                            <div className='h-px bg-burgundy/50 mx-4' />
                        </>
                    )}
                    <div className='flex flex-row items-center gap-3 p-4'>
                        <Calendar className='shrink-0 w-4 h-4 text-burgundy' />
                        <span className='text-sm text-burgundy font-light'>{startTimeLocal.format("EEEE, d. LLLL yyyy", { locale: getDateLocaleByLocale(language) })}</span>
                    </div>
                    <div className='h-px bg-burgundy/50 mx-4' />
                    <div className='flex flex-row items-center gap-3 p-4'>
                        <Clock className='shrink-0 w-4 h-4 text-burgundy' />
                        <span className='text-sm text-burgundy font-light'>{startTimeLocal.format('HH:mm')} - {endTimeLocal.format('HH:mm')} Uhr</span>
                    </div>
                </div>
            </motion.div>

            {/* Customer Details */}
            <motion.div
                className='flex flex-col gap-3'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2, ease: "easeOut" }}
            >
                <span className='text-lg font-normal text-burgundy'>Kunde</span>
                <div className='rounded-[5px] bg-white border-2 border-burgundy/20 drop-shadow-md overflow-hidden'>
                    <div className='flex flex-row items-center gap-3 p-4'>
                        <User className='shrink-0 w-4 h-4 text-burgundy' />
                        <span className='text-sm text-burgundy font-light'>{appointment.customer?.firstName} {appointment.customer?.lastName}</span>
                    </div>
                    {appointment.customer?.email && (
                        <>
                            <div className='h-px bg-burgundy/50 mx-4' />
                            <div className='flex flex-row items-center gap-3 p-4'>
                                <Mail className='shrink-0 w-4 h-4 text-burgundy' />
                                <span className='text-sm text-burgundy font-light'>{appointment.customer.email}</span>
                            </div>
                        </>
                    )}
                    {appointment.customer?.phone && (
                        <>
                            <div className='h-px bg-burgundy/50 mx-4' />
                            <div className='flex flex-row items-center gap-3 p-4'>
                                <Phone className='shrink-0 w-4 h-4 text-burgundy' />
                                <span className='text-sm text-burgundy font-light'>{appointment.customer.phone}</span>
                            </div>
                        </>
                    )}
                </div>
            </motion.div>

            {/* Services */}
            <motion.div
                className='flex flex-col gap-3'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.25, ease: "easeOut" }}
            >
                <span className='text-lg font-normal text-burgundy'>Behandlungen</span>
                <div className='rounded-[5px] bg-white border-2 border-burgundy/20 drop-shadow-md overflow-hidden'>
                    {appointment.variants && appointment.variants.length > 0 ? (
                        appointment.variants.map((variant, index: number) => (
                            <div key={variant.id ?? index}>
                                {index > 0 && <div className='h-px bg-burgundy/50 mx-4' />}
                                <div className='flex flex-row items-center justify-between gap-4 p-4'>
                                    <span className='text-sm text-burgundy font-light'>{variant.name}</span>
                                    <Tag className='w-4 h-4 text-burgundy/50' />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className='p-4'>
                            <span className='text-sm text-burgundy/70 font-light'>Keine spezifischen Leistungen aufgeführt</span>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Messages */}
            {appointment.messageToCustomer && (
                <motion.div
                    className='flex flex-col gap-3'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3, ease: "easeOut" }}
                >
                    <span className='text-lg font-normal text-burgundy'>Nachricht vom Anbieter</span>
                    <div className='rounded-[5px] bg-white border-2 border-burgundy/20 p-4 drop-shadow-md'>
                        <p className='text-sm text-burgundy font-light whitespace-pre-wrap'>{appointment.messageToCustomer}</p>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

const Appointment = () => {
    const { language } = useLanguageStore();

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get("id");
    const [viewMode, setViewMode] = useState<'details' | 'changing'>('details');

    const [newSelectedDate, setNewSelectedDate] = useState<LocalDateTime | undefined>(undefined);

    const [cancelFormOpen, setCancelFormOpen] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [pendingCancel, setPendingCancel] = useState<{ reason: string; note: string } | null>(null);

    const appointmentQuery = trpc.publicAppointments.getAppointment.useQuery(
        {
            appointmentId: id || '',
        },
        {
            enabled: !!id,
            staleTime: 0 * 60 * 1000,
        }
    );

    const cancelAppointmentMutation = trpc.publicAppointments.cancelAppointment.useMutation({
        onSuccess: () => {
            appointmentQuery.refetch();
            toast.success('Termin wurde erfolgreich abgesagt');
        },
        onError: () => {
            toast.error('Der Termin konnte nicht abgesagt werden. Bitte versuchen Sie es erneut.');
        }
    });

    const rescheduleAppointmentMutation = trpc.publicAppointments.rescheduleAppointment.useMutation({
        onSuccess: () => {
            appointmentQuery.refetch();
            setViewMode('details');
            toast.success('Termin wurde erfolgreich geändert');
        },
        onError: () => {
            toast.error('Der Termin konnte nicht geändert werden. Bitte versuchen Sie es erneut.');
        }
    });

    const { data: appointment } = appointmentQuery;

    const timeBeforeStartAllowedToModifyOrCancelHrs = 48;
    const startTimeDate = !!appointment ? LocalDateTime.deserialize(appointment.startTimeLocalSerialized) : LocalDateTime.fromUTCDate(new Date(), 'Europe/Berlin');
    const tooLateToChange = !(startTimeDate.toUTC().getTime() - new Date().getTime() > 1000 * 60 * 60 * timeBeforeStartAllowedToModifyOrCancelHrs);
    const canBeModified = !!appointment && (appointment.status === 'REQUESTED' || appointment.status === 'SCHEDULED') && !tooLateToChange;

    const originalVariantIds = useMemo(() => appointment?.variants?.map((v) => v.id) || [], [appointment?.variants]);

    const { sendEvent } = useGTM();
    const [preferredPractitionerId, setPreferredPractitionerId] = useState<number | null>(null);

    const pastPractitionersQuery = trpc.publicAppointments.getPastPractitionersForAppointment.useQuery(
        {
            appointmentId: id || '',
        },
        {
            enabled: !!id && canBeModified,
            staleTime: 10 * 60 * 1000,
        }
    );
    const pastPractitioners = pastPractitionersQuery.data ?? [];

    useEffect(() => {
        if (!preferredPractitionerId) {
            return;
        }
        if (pastPractitionersQuery.isFetched && !pastPractitioners.some((practitioner) => practitioner.id === preferredPractitionerId)) {
            setPreferredPractitionerId(null);
        }
    }, [pastPractitioners, pastPractitionersQuery.isFetched, preferredPractitionerId]);

    const handlePractitionerChange = (practitioner: PastPractitionerOption | null) => {
        const changedPractitioner = practitioner ?? pastPractitioners.find((option) => option.id === preferredPractitionerId);
        setPreferredPractitionerId(practitioner?.id ?? null);
        setNewSelectedDate(undefined);

        if (!changedPractitioner) {
            return;
        }

        sendEvent(practitioner ? GTMEvent.APPOINTMENT_MODIFICATION_PRACTITIONER_SELECTED : GTMEvent.APPOINTMENT_MODIFICATION_PRACTITIONER_DESELECTED, {
            entry_point: 'appointment_modification',
            deeplink_slug: undefined,
            deeplink_practitioner_id: undefined,
            deeplink_category_id: undefined,
            preferred_practitioner_id: practitioner?.id,
            preferred_practitioner_selected: !!practitioner,
            appointment_id: appointment?.id,
            practitioner_id: changedPractitioner.id,
            practitioner_name: formatPractitionerName(changedPractitioner),
            practitioner_type: changedPractitioner.type,
            branch: {
                id: appointment?.store?.id,
                name: appointment?.store?.name,
            },
            variant_ids: originalVariantIds,
        });
    };

    const handleGoToChange = () => {
        setNewSelectedDate(undefined);
        setViewMode('changing');
    };

    const handleGoBackToDetails = () => {
        setViewMode('details');
    };

    const handleConfirmChange = useCallback(async (reason: string, note: string) => {
        if (!newSelectedDate || !appointment) {
            toast.error("Bitte wählen Sie zuerst ein neues Datum und eine Uhrzeit aus.");
            return;
        }

        try {
            await rescheduleAppointmentMutation.mutateAsync({
                appointmentId: appointment.id,
                selectedStartTime: newSelectedDate.toJson(),
                reason,
                note,
                ...(preferredPractitionerId ? { practitionerId: preferredPractitionerId } : {}),
            });
        } catch (error) {
            console.error("Failed to change appointment:", error);
        }
    }, [appointment, newSelectedDate, preferredPractitionerId, rescheduleAppointmentMutation]);

    const handleCancelAppointment = (reason: string, note: string) => {
        if (!id) { return; }
        setPendingCancel({ reason, note });
        setCancelFormOpen(false);
        setCancelConfirmOpen(true);
    };

    const handleConfirmCancelAppointment = async () => {
        if (!id || !pendingCancel) { return; }
        try {
            await cancelAppointmentMutation.mutateAsync({
                appointmentId: id,
                reason: pendingCancel.reason,
                note: pendingCancel.note,
            });
        } catch (error) {
            console.error("Failed to cancel appointment:", error);
        } finally {
            setCancelConfirmOpen(false);
            setPendingCancel(null);
        }
    };

    // Loading State
    if (appointmentQuery.isLoading || !id) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="TERMIN" />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <Loader2 className='h-8 w-8 animate-spin text-burgundy' />
                    <span className='text-sm text-burgundy/70 font-light mt-4'>Laden...</span>
                </div>
            </motion.div>
        );
    }

    // Error State
    if (appointmentQuery.isError || !appointment) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString="TERMIN" onClickBackButton={() => navigate('/')} />
                <div className='flex flex-col items-center justify-center flex-1 px-6 text-center'>
                    <div className='flex flex-col gap-4'>
                        <span className='text-xl text-red-600 font-normal'>
                            Fehler
                        </span>
                        <span className='text-sm text-burgundy/70 font-light'>
                            {appointmentQuery.isError
                                ? 'Termindetails konnten nicht geladen werden. Bitte versuchen Sie es später erneut.'
                                : 'Der angeforderte Termin konnte nicht gefunden werden.'}
                        </span>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            className='flex flex-col min-h-screen'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <TopProgressBar
                percentageFilled={viewMode === 'details' ? 100 : 50}
                displayString={viewMode === 'details' ? 'TERMINDETAILS' : 'TERMIN ÄNDERN'}
                onClickBackButton={viewMode === 'changing' ? handleGoBackToDetails : undefined}
            />

            <div className={`flex-1 overflow-y-auto px-4 pt-4 ${viewMode === 'changing' ? 'pb-48' : 'pb-8'}`}>
                {/* Title */}
                <motion.div
                    className='flex flex-col gap-3 mb-6'
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.05, ease: "easeOut" }}
                >
                    <h1 className='text-2xl text-burgundy text-center'>
                        {viewMode === 'details' ? 'Termindetails' : 'Neues Datum auswählen'}
                    </h1>
                    {!canBeModified && viewMode === 'details' && (
                        <div className='rounded-[5px] bg-yellow-50 border border-yellow-200 p-4'>
                            <span className='text-sm text-yellow-700 font-light'>
                                {tooLateToChange
                                    ? `Dieser Termin findet in weniger als ${timeBeforeStartAllowedToModifyOrCancelHrs} Stunden statt und kann nicht mehr geändert oder storniert werden.`
                                    : 'Dieser Termin kann nicht geändert werden.'
                                }
                            </span>
                        </div>
                    )}
                </motion.div>

                {/* Content */}
                <div className='w-full flex flex-col gap-6'>
                    {viewMode === 'details' ? (
                        <AppointmentDetails appointment={appointment} />
                    ) : (
                        <div className='flex flex-col gap-2'>
                            {pastPractitioners.length > 0 && (
                                <PastPractitionerPickerRow
                                    practitioners={pastPractitioners}
                                    selectedPractitionerId={preferredPractitionerId}
                                    onChange={handlePractitionerChange}
                                />
                            )}
                            <ChangeAppointmentWidget
                                key={preferredPractitionerId ?? 'none'}
                                appointmentId={id}
                                storeId={appointment.store?.id || 'digital'}
                                onSelectDate={setNewSelectedDate}
                                variantIds={originalVariantIds}
                                practitionerId={preferredPractitionerId}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Bar */}
            {viewMode === 'details' ? (
                <div className='px-6 pb-6'>
                    <FixedBottomBar
                        viewMode={viewMode}
                        onChangeAppointment={handleGoToChange}
                        onCancelAppointment={handleCancelAppointment}
                        cancelFormOpen={cancelFormOpen}
                        onCancelFormOpenChange={setCancelFormOpen}
                        onConfirmChange={handleConfirmChange}
                        onBackToDetails={handleGoBackToDetails}
                        canBeModified={canBeModified}
                        hasSelectedNewTime={!!newSelectedDate}
                        isLoading={rescheduleAppointmentMutation.isPending}
                    />
                </div>
            ) : (
                <FixedBottomBar
                    viewMode={viewMode}
                    onChangeAppointment={handleGoToChange}
                    onCancelAppointment={handleCancelAppointment}
                    cancelFormOpen={cancelFormOpen}
                    onCancelFormOpenChange={setCancelFormOpen}
                    onConfirmChange={handleConfirmChange}
                    onBackToDetails={handleGoBackToDetails}
                    canBeModified={canBeModified}
                    hasSelectedNewTime={!!newSelectedDate}
                    isLoading={rescheduleAppointmentMutation.isPending}
                />
            )}

            {/* Final confirmation to cancel appointment */}
            <AlertDialog open={cancelConfirmOpen} onOpenChange={(open) => { setCancelConfirmOpen(open); if (!open) setPendingCancel(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-burgundy">Termin absagen</AlertDialogTitle>
                        <AlertDialogDescription className="text-burgundy/70">
                            Sind Sie sicher, dass Sie Ihren Termin absagen möchten?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex flex-row gap-2 sm:justify-end">
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); void handleConfirmCancelAppointment(); }}
                            disabled={cancelAppointmentMutation.isPending}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {cancelAppointmentMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                                    Wird abgesagt...
                                </>
                            ) : (
                                'Ja, Termin absagen'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </motion.div>
    );
};

export default Appointment;
